import { CommonModule } from '@angular/common';
import { Component, Input, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { QaiTableComponent, QaiLoadingComponent, TableRow, HeaderConfig, SortDirection } from '@qai/ui';
import { ICertificationSimulation } from 'src/app/interfaces/reportingV2/icertification-simulation';
import { TranslationService } from 'src/app/services/translation.service';
import { Subscription } from 'rxjs';
import { ReportingDashboardService } from 'src/app/services/reporting-dashboard/reporting-dashboard.service';
import { ReportingLocalStorageUtil } from 'src/app/utils/reporting-localstorage.util';
import { LocaleService } from 'src/app/services/locale.service';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { withDemoModule } from 'src/app/utils/demo-mode/demo-mode-context.util';
import { AppConstants } from 'src/app/app-constants';

@Component({
  selector: 'app-certification-simulations',
  templateUrl: './certification-simulations.component.html',
  styleUrls: ['./certification-simulations.component.scss'],
  standalone: true,
  imports: [TranslocoModule, CommonModule, QaiTableComponent, QaiLoadingComponent]
})
export class CertificationSimulationsComponent {
  @Input() assignmentId: string | null = null;
  @Input() selectedTeamIds: string[] = [];
  @Input() assignmentName: string = '';
  @Input() engagementStatus: string | null = null;

  isTranslationLoaded: boolean = false;
  isLoading: boolean = false;
  tableData: TableRow[] = [];
  headers: HeaderConfig[] = [];
  initialSortColumn: string = '';
  initialSortDirection: SortDirection = 'asc';
  currentPage: number = 1;
  pageSize: number = 3;
  accountUuid: string | null = null;

  certificationSimulations: ICertificationSimulation[] = [];
  private certificationSimulationSub?: Subscription;

  constructor(
    private translocoService: TranslocoService,
    private translationService: TranslationService,
    private reportingDashboardService: ReportingDashboardService,
    private localeService: LocaleService,
    private router: Router,
    private amplitudeService: AmplitudeService
  ) { }

  ngOnInit() {
    this.translationService.loadScopedTranslation('reportingV2').then(() => {
      this.isTranslationLoaded = true;
      this.accountUuid = ReportingLocalStorageUtil.getStoredAccountUuid();
      this.initializeTable();
      this.loadSimulations();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if ((changes['assignmentId'] && !changes['assignmentId'].firstChange) || (changes['selectedTeamIds'] && !changes['selectedTeamIds'].firstChange)) 
    {
      this.loadSimulations();
    }
  }

  private initializeTable() {
    this.headers = [
      {
        name: this.translocoService.translate('SIMULATION_NAME', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '20%'
      },
      {
        name: this.translocoService.translate('USERS_PASSED', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '23%'
      },
      {
        name: this.translocoService.translate('AVERAGE_TIME', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '20%'
      },
      {
        name: this.translocoService.translate('AVERAGE_SCORE_LABEL', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '17%'
      },
      {
        name: this.translocoService.translate('USERS_OUT_OF_ATTEMPTS', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '20%'
      }
    ];

    this.initialSortColumn = this.translocoService.translate('SIMULATION_NAME', {}, 'reportingV2');
    this.initialSortDirection = 'asc';
  }

  private loadSimulations() {
    if (this.certificationSimulationSub) {
      this.certificationSimulationSub.unsubscribe();
    }

    if (!this.assignmentId || !this.accountUuid) {
      this.isLoading = false;
      return;
    }

    this.isLoading = true;

    this.certificationSimulationSub = this.reportingDashboardService
      .getCertificationSimulationMetrics(
        this.accountUuid,
        this.assignmentId,
        this.selectedTeamIds.includes('all') ? [] : this.selectedTeamIds,
        withDemoModule(AppConstants.NEW_REPORTING_UI)
      )
      .subscribe({
        next: (simulations: ICertificationSimulation[]) => {
          this.certificationSimulations = simulations;
          this.tableData = this.createTableRows(simulations);
          this.currentPage = 1;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Failed to load simulation metrics:', error);
          this.isLoading = false;
        },
      });
  }


  private createTableRows(simulations: ICertificationSimulation[]): TableRow[] {
    const safeValue = (val: any, formatter?: (v: any) => string): string => {
      if (val === null || val === undefined) {
        return '—';
      }
      return formatter ? formatter(val) : val.toString();
    };

    return simulations.map(simulation => {
      const row: any = {
        id: simulation.uuid,
        [this.translocoService.translate('SIMULATION_NAME', {}, 'reportingV2')]: {
          type: 'text',
          value: safeValue(simulation.simulationName)
        },
        [this.translocoService.translate('USERS_PASSED', {}, 'reportingV2')]: {
          type: 'text',
          value:
            simulation.percentPassed == null
              ? '—'
              : `${this.localeService.formatNumber(simulation.percentPassed)}%`
        },
        [this.translocoService.translate('AVERAGE_TIME', {}, 'reportingV2')]: {
          type: 'text',
          value: safeValue(
            simulation.avgTimeSecsToCertify,
            v => this.formatDuration(v)
          )
        },
        [this.translocoService.translate('AVERAGE_SCORE_LABEL', {}, 'reportingV2')]: {
          type: 'text',
          value: safeValue(simulation.avgScore, v => this.localeService.formatNumber(v, 1))
        },
        [this.translocoService.translate('USERS_OUT_OF_ATTEMPTS', {}, 'reportingV2')]: {
          type: 'text',
          value: safeValue(simulation.usersOutOfAttempts, v => this.localeService.formatNumber(v))
        }
      };

      return row as TableRow;
    });
  }

  private formatDuration(seconds: number | null | undefined): string {
    if (seconds == null || isNaN(seconds)) return '—';

    const totalSeconds = Math.floor(seconds);
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;

    const minLabel = this.translocoService.translate(
      minutes === 1 ? 'MINUTE' : 'MINUTES',
      {},
      'reportingV2'
    );
    const secLabel = this.translocoService.translate(
      remainingSeconds === 1 ? 'SECOND' : 'SECONDS',
      {},
      'reportingV2'
    );

    const formattedMinutes = this.localeService.formatNumber(minutes);
    const formattedSeconds = this.localeService.formatNumber(remainingSeconds);

    if (minutes > 0 && remainingSeconds > 0) {
      return `${formattedMinutes} ${minLabel} ${formattedSeconds} ${secLabel}`;
    } else if (minutes > 0) {
      return `${formattedMinutes} ${minLabel}`;
    } else {
      return `${formattedSeconds} ${secLabel}`;
    }
  }

  onTableRowClick(event: { rowId: string | number; rowData: TableRow }): void {
    const simulation = this.certificationSimulations.find(sim => sim.uuid === event.rowId);
    
    if (simulation) {
      this.amplitudeService.trackEvent(
        AmplitudeTrackingEvents.ASSIGNMENTREPORT_CLICK_SIMULATIONTABLE_SIMNAME,
        { simulation_name: simulation.simulationName }
      );
      
      this.router.navigate(['reporting_dashboard/simulation', event.rowId], {
        queryParams: {
          moduleAssignmentUuid: this.assignmentId
        },
        state: {
          assignmentName: this.assignmentName,
          simulationName: simulation.simulationName,
          selectedTeamUuids: this.selectedTeamIds ?? [],
          engagementStatus: this.engagementStatus
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.certificationSimulationSub?.unsubscribe();
  }

}
